import React from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CONTACT_EMAIL } from "@/config";
import { Ban, CheckCircle2, Clock, Mail, ArrowLeft } from "lucide-react";

export const REFUND_WINDOW_DAYS = 7;
export const FIX_WINDOW_DAYS = 3;
const UPDATED = "June 2026";

const EXCEPTIONS = [
  { title: "Duplicate charge", body: "You were charged more than once for the same beat. We refund the extra charge in full." },
  { title: "Files not delivered or corrupted", body: `Your download is missing, broken or won't open. We'll fix or re-deliver it within ${FIX_WINDOW_DAYS} days. If we can't, you get a full refund.` },
  { title: "Unauthorized charge", body: "Someone used your card without permission. We refund it and cancel the license so the beat can't be used." },
];

const NOT_ELIGIBLE = [
  "Changing your mind, or no longer needing the beat",
  "Buying the wrong beat by mistake after the files were downloaded",
  "Your song, release or project not working out",
  "Not reading the beat details (BPM, key, genre, included files) before buying",
  "Problems with your own software, device or internet connection",
];

const Section = ({ n, title, children }) => (
  <section className="border-t border-[#2a2a2a] pt-8">
    <h2 className="font-heading text-lg font-semibold mb-3"><span className="text-[#e2ff31] font-mono mr-2">{n}.</span>{title}</h2>
    <div className="text-[#9ca3af] leading-relaxed space-y-3 text-sm sm:text-base">{children}</div>
  </section>
);

export default function RefundPolicy() {
  return (
    <div className="min-h-screen grain">
      <Navbar />
      <main data-testid="refund-policy-page" className="relative z-10 max-w-3xl mx-auto px-6 pt-28 pb-16 space-y-8">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#9ca3af] hover:text-[#e2ff31] transition-colors"><ArrowLeft className="w-4 h-4" /> Back to the store</Link>
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#e2ff31]">Policy</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mt-2">Refund Policy</h1>
          <p className="text-sm text-[#6b7280] mt-3">Last updated {UPDATED}</p>
        </div>

        <div data-testid="refund-summary" className="rounded-2xl border border-[#ef4444]/30 bg-[#ef4444]/5 p-5 flex gap-4">
          <Ban className="w-6 h-6 text-[#ef4444] shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">All sales are final. No refunds.</p>
            <p className="text-sm text-[#9ca3af] mt-1">
              Every beat is a one-of-one exclusive. The moment you buy it, it's removed from the store forever and the files are yours instantly,
              so a sale can't be undone. The only exceptions are the three listed below.
            </p>
          </div>
        </div>

        <Section n={1} title="Why there are no refunds">
          <p>You're buying digital files and full exclusive rights. Once a beat sells, it's delivered right away, taken off the store and can never be sold to anyone else. We can't take a beat "back" after that, so every sale is final.</p>
          <p>Please preview the beat fully and check its BPM, key, genre and included files (MP3, WAV, stems) before you buy.</p>
        </Section>

        <Section n={2} title="When you CAN get a refund">
          <div className="grid gap-3">
            {EXCEPTIONS.map((e) => (
              <div key={e.title} className="flex gap-3 rounded-xl border border-[#2a2a2a] bg-[#121212] p-4">
                <CheckCircle2 className="w-5 h-5 text-[#22c55e] shrink-0 mt-0.5" />
                <div><p className="text-[#f3f4f6] font-medium">{e.title}</p><p className="text-sm mt-0.5">{e.body}</p></div>
              </div>
            ))}
          </div>
        </Section>

        <Section n={3} title="Not eligible for a refund">
          <ul className="list-disc pl-5 space-y-1.5">{NOT_ELIGIBLE.map((x) => <li key={x}>{x}</li>)}</ul>
        </Section>

        <Section n={4} title="How to request a refund">
          <div className="flex items-start gap-3"><Clock className="w-5 h-5 text-[#e2ff31] shrink-0 mt-0.5" />
            <p>Email us within <strong className="text-[#f3f4f6]">{REFUND_WINDOW_DAYS} days of your purchase</strong>. Requests after {REFUND_WINDOW_DAYS} days can't be accepted.</p></div>
          <div className="flex items-start gap-3"><Mail className="w-5 h-5 text-[#e2ff31] shrink-0 mt-0.5" />
            <p>Send it to <a data-testid="refund-contact-email" href={`mailto:${CONTACT_EMAIL}?subject=Refund%20request`} className="text-[#e2ff31] hover:underline">{CONTACT_EMAIL}</a> with your order email, the beat title, the date of purchase and what went wrong (screenshots help).</p></div>
          <p>We reply within 2 business days. Approved refunds go back to your original payment method through Stripe and usually show up in 5–10 business days, depending on your bank.</p>
        </Section>

        <Section n={5} title="What happens to the license after a refund">
          <p>If a refund is approved, your exclusive license is cancelled. You must stop using the beat and delete all copies of the files. Any song already released with it must be taken down.</p>
        </Section>

        <Section n={6} title="Chargebacks">
          <p>Please contact us before opening a dispute with your bank — it's the fastest way to sort out a problem. Opening a chargeback for a valid sale cancels your license, and we may share proof of delivery (download records) with your bank.</p>
        </Section>

        <Section n={7} title="Questions">
          <p>Anything unclear? Email <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#e2ff31] hover:underline">{CONTACT_EMAIL}</a> before you buy and we'll help.</p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
